import type {
  Document,
  DocumentFile,
  PdfBookmark,
  PdfDocumentLink,
  PdfMarker,
  PdfMarkerRectangle,
  DocumentListResponse,
  CreateDocumentDto,
  UpdateDocumentDto,
  FollowUp,
  FollowUpItem,
  FollowUpHistoryAvailability,
  FollowUpHistoryEntry,
  ChronologyTree,
  Tag,
  TagGroup,
  SharedUser,
  UserSettingEntry,
  MeResponse,
  StatisticsResponse,
  Rule,
  RuleApplicationResponse,
  FormDefinition,
  FormFieldType,
  FormViewDefinition,
  FormViewData,
  UploadConfig,
  MaintenanceInfo,
  MaintenanceProgressEvent,
  ArchiveExportJobStatus,
  BackupCapabilities,
  BackupInspection,
  BackupJobStatus,
  BackupRestoreResult,
  BackupUserMapping,
  RestoreResetChallenge,
  RestoreResetResult,
  ImportProtocol,
  ImportProtocolEntry,
  CamtAnalyticsResponse,
  Pop3EmailAccountResponse,
  Pop3EmailConnectionTestResponse,
  Pop3EmailRetrieveResponse,
} from './api.types';
import { apiUrl, getBffBaseUrl } from './runtime-config';

const BASE = getBffBaseUrl();

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...options?.headers },
    ...options,
  });
  if (res.status === 401) {
    // Session expired or not logged in — navigate to the BFF login endpoint.
    // This triggers a full OIDC redirect to the identity provider.
    window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
    // Return a promise that never resolves; the page is navigating away.
    return new Promise<T>(() => {});
  }
  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      const text = await res.text().catch(() => '');
      if (text) message += `: ${text}`;
    }
    throw new Error(message);
  }
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

function getDownloadFileName(disposition: string, fallback: string): string {
  const encodedMatch = /(?:^|;)\s*filename(?:\*|_)\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(disposition);
  if (encodedMatch) {
    const encodedValue = (encodedMatch[1] ?? encodedMatch[2] ?? '').trim();
    const separator = encodedValue.indexOf("''");
    const value = separator >= 0 ? encodedValue.slice(separator + 2) : encodedValue;
    try {
      return decodeURIComponent(value).trim() || fallback;
    } catch (error) {
      if (error instanceof URIError)
        return value.trim() || fallback;
      throw error;
    }
  }

  const plainMatch = /(?:^|;)\s*filename\s*=\s*(?:"([^"]*)"|([^;]*))/i.exec(disposition);
  return (plainMatch?.[1] ?? plainMatch?.[2] ?? '').trim() || fallback;
}

// ── Documents ──────────────────────────────────────────────────────────────

export function listDocuments(
  sortBy = 'createdAt',
  sortDescending = true,
  page = 1,
  pageSize = 25,
  dateFrom?: string,
  dateTo?: string,
  searchText?: string,
  includeDescription = false,
  tagIds?: string[],
  ownedOnly = false,
  shareFilterType?: number,
  shareFilterUsernames?: string[],
  includeFileText = false,
  includeFormOnly = false,
): Promise<DocumentListResponse> {
  const params = new URLSearchParams({
    sortBy,
    sortDescending: String(sortDescending),
    page: String(page),
    pageSize: String(pageSize),
  });
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo)   params.set('dateTo', dateTo);
  if (searchText) params.set('searchText', searchText);
  if (includeDescription) params.set('includeDescription', 'true');
  if (tagIds && tagIds.length > 0) params.set('tagIds', tagIds.join(','));
  if (ownedOnly) params.set('ownedOnly', 'true');
  if (shareFilterType && shareFilterUsernames && shareFilterUsernames.length > 0) {
    params.set('shareFilterType', String(shareFilterType));
    for (const username of shareFilterUsernames) params.append('shareFilterUsernames', username);
  }
  if (includeFileText) params.set('includeFileText', 'true');
  if (includeFormOnly) params.set('includeFormOnly', 'true');
  return request<DocumentListResponse>(`/documents?${params}`);
}

export function getChronologyTree(
  searchText?: string,
  includeDescription = false,
  tagIds?: string[],
  ownedOnly = false,
  shareFilterType?: number,
  shareFilterUsernames?: string[],
  includeFileText = false,
  includeFormOnly = false,
): Promise<ChronologyTree> {
  const params = new URLSearchParams();
  if (searchText) params.set('searchText', searchText);
  if (includeDescription) params.set('includeDescription', 'true');
  if (tagIds && tagIds.length > 0) params.set('tagIds', tagIds.join(','));
  if (ownedOnly) params.set('ownedOnly', 'true');
  if (shareFilterType && shareFilterUsernames && shareFilterUsernames.length > 0) {
    params.set('shareFilterType', String(shareFilterType));
    for (const username of shareFilterUsernames) params.append('shareFilterUsernames', username);
  }
  if (includeFileText) params.set('includeFileText', 'true');
  if (includeFormOnly) params.set('includeFormOnly', 'true');
  const qs = params.toString();
  return request<ChronologyTree>(`/documents/chronology${qs ? '?' + qs : ''}`);
}

export function getDocument(id: string): Promise<Document> {
  return request<Document>(`/documents/${id}`);
}

export function getStatistics(): Promise<StatisticsResponse> {
  return request<StatisticsResponse>('/statistics');
}

export function getCamtAnalytics(
  documentId: string,
  fileId: string,
): Promise<CamtAnalyticsResponse> {
  return request<CamtAnalyticsResponse>(
    `/documents/${documentId}/files/${fileId}/camt-analytics`,
  );
}

export function createDocument(dto: CreateDocumentDto): Promise<Document> {
  return request<Document>('/documents', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateDocument(id: string, dto: UpdateDocumentDto): Promise<Document> {
  return request<Document>(`/documents/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deleteDocument(id: string): Promise<void> {
  return request<void>(`/documents/${id}`, { method: 'DELETE' });
}

// ── Import protocols ───────────────────────────────────────────────────────

export function createImportProtocol(): Promise<ImportProtocol> {
  return request<ImportProtocol>('/import-protocols', {
    method: 'POST',
    body: '{}',
  });
}

export function completeImportProtocol(number: number): Promise<ImportProtocol> {
  return request<ImportProtocol>(`/import-protocols/${number}/complete`, {
    method: 'POST',
    body: JSON.stringify({ completedAt: new Date().toISOString() }),
  });
}

export function listImportProtocols(page = 1, pageSize = 25): Promise<ImportProtocol[]> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  return request<ImportProtocol[]>(`/import-protocols?${params}`);
}

export function getImportProtocol(number: number): Promise<ImportProtocol> {
  return request<ImportProtocol>(`/import-protocols/${number}`);
}

export function addImportProtocolEntry(
  number: number,
  entry: Omit<ImportProtocolEntry, 'id' | 'protocolNumber'>,
): Promise<ImportProtocolEntry> {
  return request<ImportProtocolEntry>(`/import-protocols/${number}/entries`, {
    method: 'POST',
    body: JSON.stringify(entry),
  });
}

export function deleteImportProtocol(number: number): Promise<void> {
  return request<void>(`/import-protocols/${number}`, { method: 'DELETE' });
}

export function shareDocuments(documentIds: string[], usernames: string[]): Promise<void> {
  return request<void>('/documents/share', { method: 'POST', body: JSON.stringify({ documentIds, usernames }) });
}

export function unshareDocument(documentId: string, username: string): Promise<void> {
  return request<void>(`/documents/${documentId}/share/${encodeURIComponent(username)}`, { method: 'DELETE' });
}

export function getDocumentShares(documentId: string): Promise<SharedUser[]> {
  return request<SharedUser[]>(`/documents/${documentId}/shares`);
}

export function uploadFile(documentId: string, file: File): Promise<DocumentFile> {
  const form = new FormData();
  form.append('file', file);
  return request<DocumentFile>(`/documents/${documentId}/files`, { method: 'POST', body: form, headers: {} });
}

export async function readDocumentFileText(documentId: string, fileId: string): Promise<string> {
  const res = await fetch(`${BASE}/documents/${documentId}/files/${fileId}/download`, {
    credentials: 'include',
  });
  if (res.status === 401) {
    window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
    return new Promise<string>(() => {});
  }
  if (!res.ok) {
    let message = `API error ${res.status}`;
    const text = await res.text().catch(() => '');
    if (text) message += `: ${text}`;
    throw new Error(message);
  }
  return res.text();
}

export function getPdfMarkers(documentId: string, fileId: string): Promise<PdfMarker[]> {
  return request<PdfMarker[]>(`/documents/${documentId}/files/${fileId}/markers`);
}

export function createPdfMarker(
  documentId: string,
  fileId: string,
  marker: {
    pageNumber: number;
    selectedText: string;
    color: string;
    comment?: string;
    rectangles: PdfMarkerRectangle[];
  },
): Promise<PdfMarker> {
  return request<PdfMarker>(`/documents/${documentId}/files/${fileId}/markers`, {
    method: 'POST',
    body: JSON.stringify(marker),
  });
}

export function updatePdfMarkerComment(
  documentId: string, fileId: string, markerId: string, comment: string, color?: string,
): Promise<PdfMarker> {
  return request<PdfMarker>(`/documents/${documentId}/files/${fileId}/markers/${markerId}/comment`, {
    method: 'PUT',
    body: JSON.stringify({ comment, color }),
  });
}

export function deletePdfMarker(documentId: string, fileId: string, markerId: string): Promise<void> {
  return request<void>(`/documents/${documentId}/files/${fileId}/markers/${markerId}`, {
    method: 'DELETE',
  });
}

export function deleteAllPdfMarkers(
  documentId: string,
  fileId: string,
): Promise<{ deletedCount: number }> {
  return request<{ deletedCount: number }>(`/documents/${documentId}/files/${fileId}/markers`, {
    method: 'DELETE',
  });
}

export function getPdfBookmarks(): Promise<PdfBookmark[]> {
  return request<PdfBookmark[]>('/bookmarks');
}

export function getPdfBookmarksForFile(documentId: string, fileId: string): Promise<PdfBookmark[]> {
  return request<PdfBookmark[]>(`/documents/${documentId}/files/${fileId}/bookmarks`);
}

export function createPdfBookmark(
  documentId: string,
  fileId: string,
  bookmark: {
    pageNumber: number;
    selectedText?: string;
    title: string;
    description?: string;
    rectangles?: PdfMarkerRectangle[];
    tagNames?: string[];
  },
): Promise<PdfBookmark> {
  return request<PdfBookmark>(`/documents/${documentId}/files/${fileId}/bookmarks`, {
    method: 'POST',
    body: JSON.stringify(bookmark),
  });
}

export function updatePdfBookmark(
  documentId: string,
  fileId: string,
  bookmarkId: string,
  bookmark: {
    title: string;
    description?: string;
    tagNames?: string[];
  },
): Promise<PdfBookmark> {
  return request<PdfBookmark>(`/documents/${documentId}/files/${fileId}/bookmarks/${bookmarkId}`, {
    method: 'PUT',
    body: JSON.stringify(bookmark),
  });
}

export function deletePdfBookmark(
  documentId: string,
  fileId: string,
  bookmarkId: string,
): Promise<void> {
  return request<void>(`/documents/${documentId}/files/${fileId}/bookmarks/${bookmarkId}`, {
    method: 'DELETE',
  });
}

export function getPdfDocumentLinks(documentId: string, fileId: string): Promise<PdfDocumentLink[]> {
  return request<PdfDocumentLink[]>(`/documents/${documentId}/files/${fileId}/document-links`);
}

export function createPdfDocumentLink(
  documentId: string,
  fileId: string,
  link: {
    pageNumber: number;
    selectedText: string;
    rectangles: PdfMarkerRectangle[];
    targetDocumentId: string;
  },
): Promise<PdfDocumentLink> {
  return request<PdfDocumentLink>(`/documents/${documentId}/files/${fileId}/document-links`, {
    method: 'POST',
    body: JSON.stringify(link),
  });
}

export function deletePdfDocumentLink(
  documentId: string,
  fileId: string,
  linkId: string,
): Promise<void> {
  return request<void>(`/documents/${documentId}/files/${fileId}/document-links/${linkId}`, {
    method: 'DELETE',
  });
}

export function replaceFile(documentId: string, fileId: string, file: File): Promise<DocumentFile> {
  const form = new FormData();
  form.append('file', file);
  return request<DocumentFile>(`/documents/${documentId}/files/${fileId}`, { method: 'PUT', body: form, headers: {} });
}

export function removeFile(documentId: string, fileId: string): Promise<void> {
  return request<void>(`/documents/${documentId}/files/${fileId}`, { method: 'DELETE' });
}

/** Moves an existing file from one document to another. Returns the moved file's updated record. */
export function moveFile(sourceDocumentId: string, fileId: string, targetDocumentId: string): Promise<DocumentFile> {
  return request<DocumentFile>(`/documents/${sourceDocumentId}/files/${fileId}/move`, {
    method: 'POST',
    body: JSON.stringify({ targetDocumentId }),
  });
}

export function reorderFiles(documentId: string, fileIds: string[]): Promise<void> {
  return request<void>(`/documents/${documentId}/files/order`, { method: 'PUT', body: JSON.stringify({ fileIds }) });
}

export function ocrDocumentFile(documentId: string, fileId: string, language?: string): Promise<DocumentFile> {
  return request<DocumentFile>(`/documents/${documentId}/files/${fileId}/ocr`, { method: 'POST', body: JSON.stringify({ language: language ?? "" }) });
}

// ── Follow-Ups ─────────────────────────────────────────────────────────────

export function addFollowUp(documentId: string, dueDate: string, note?: string, forUsername?: string): Promise<FollowUp> {
  return request<FollowUp>(`/documents/${documentId}/followups`, {
    method: 'POST',
    body: JSON.stringify({ dueDate, note, forUsername }),
  });
}

export function updateFollowUp(
  documentId: string,
  followUpId: string,
  dto: { dueDate: string; note?: string; forUsername?: string },
): Promise<FollowUp> {
  return request<FollowUp>(`/documents/${documentId}/followups/${followUpId}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function removeFollowUp(documentId: string, followUpId: string): Promise<void> {
  return request<void>(`/documents/${documentId}/followups/${followUpId}`, { method: 'DELETE' });
}

export function getFollowUpHistoryAvailability(documentId: string): Promise<FollowUpHistoryAvailability> {
  return request<FollowUpHistoryAvailability>(`/documents/${documentId}/followups/history/availability`, {
    cache: 'no-store',
  });
}

export function getFollowUpHistory(documentId: string): Promise<FollowUpHistoryEntry[]> {
  return request<FollowUpHistoryEntry[]>(`/documents/${documentId}/followups/history`, {
    cache: 'no-store',
  });
}

export function listUpcomingFollowUps(daysAhead = 7): Promise<FollowUpItem[]> {
  return request<FollowUpItem[]>(`/followups/upcoming?daysAhead=${daysAhead}`);
}

// ── Tags ───────────────────────────────────────────────────────────────────

export function listTags(sortBy = 'name', sortDescending = false): Promise<Tag[]> {
  const params = new URLSearchParams({ sortBy, sortDescending: String(sortDescending) });
  return request<Tag[]>(`/tags?${params}`);
}

export function searchTags(q: string, limit = 20, offset = 0): Promise<{ tags: Tag[]; hasMore: boolean }> {
  const params = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
  return request<{ tags: Tag[]; hasMore: boolean }>(`/tags/search?${params}`);
}

export function searchUsers(q: string): Promise<{ username: string; displayName?: string }[]> {
  const params = new URLSearchParams({ q });
  return request<{ username: string; displayName?: string }[]>(`/users/search?${params}`);
}

export function createTag(dto: { name?: string; color?: string; searchText?: string; groupId?: string | null }): Promise<Tag> {
  return request<Tag>('/tags', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateTag(id: string, dto: { name?: string; color?: string; searchText?: string; groupId?: string | null }): Promise<Tag> {
  return request<Tag>(`/tags/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deleteTag(id: string): Promise<void> {
  return request<void>(`/tags/${id}`, { method: 'DELETE' });
}

export function listTagGroups(): Promise<TagGroup[]> {
  return request<TagGroup[]>('/tag-groups');
}

export function createTagGroup(name: string): Promise<TagGroup> {
  return request<TagGroup>('/tag-groups', { method: 'POST', body: JSON.stringify({ name }) });
}

export function updateTagGroup(id: string, name: string): Promise<TagGroup> {
  return request<TagGroup>(`/tag-groups/${id}`, { method: 'PUT', body: JSON.stringify({ name }) });
}

export function deleteTagGroup(id: string): Promise<void> {
  return request<void>(`/tag-groups/${id}`, { method: 'DELETE' });
}

export function reorderTagGroups(groupIds: string[]): Promise<void> {
  return request<void>('/tag-groups/reorder', { method: 'POST', body: JSON.stringify({ groupIds }) });
}

export function reorderTagsInGroup(tagIds: string[]): Promise<void> {
  return request<void>('/tags/reorder', { method: 'POST', body: JSON.stringify({ tagIds }) });
}

export function moveTagToGroup(tagId: string, groupId: string | null, position: number): Promise<Tag> {
  return request<Tag>(`/tags/${tagId}/move`, { method: 'POST', body: JSON.stringify({ groupId: groupId ?? '', position }) });
}

// ── User Settings ──────────────────────────────────────────────────────────

export function getUserSettings(): Promise<UserSettingEntry[]> {
  return request<UserSettingEntry[]>('/usersettings');
}

export function upsertUserSetting(key: string, value: string): Promise<void> {
  return request<void>(`/usersettings/${key}`, { method: 'PUT', body: JSON.stringify({ value }) });
}

// ── POP3 email account ────────────────────────────────────────────────────

export function getPop3EmailAccount(): Promise<Pop3EmailAccountResponse> {
  return request<Pop3EmailAccountResponse>('/pop3-email-account');
}

export interface UpsertPop3EmailAccountRequest {
  pop3Server: string;
  port: number;
  useSsl: boolean;
  useStartTls: boolean;
  username: string;
  password: string;
  clearPassword: boolean;
  checkIntervalMinutes: number;
  enabled: boolean;
  connectionTimeoutSeconds: number;
}

export function upsertPop3EmailAccount(
  dto: UpsertPop3EmailAccountRequest,
): Promise<Pop3EmailAccountResponse['settings']> {
  return request<Pop3EmailAccountResponse['settings']>('/pop3-email-account', {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}

export interface TestPop3EmailConnectionRequest {
  pop3Server: string;
  port: number;
  useSsl: boolean;
  useStartTls: boolean;
  username: string;
  password: string;
  connectionTimeoutSeconds: number;
}

export function testPop3EmailConnection(
  dto: TestPop3EmailConnectionRequest,
): Promise<Pop3EmailConnectionTestResponse> {
  return request<Pop3EmailConnectionTestResponse>('/pop3-email-account/test', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function retrievePop3EmailAccountNow(): Promise<Pop3EmailRetrieveResponse> {
  return request<Pop3EmailRetrieveResponse>('/pop3-email-account/retrieve', {
    method: 'POST',
    body: '{}',
  });
}

export function deletePop3EmailAccount(): Promise<void> {
  return request<void>('/pop3-email-account', { method: 'DELETE' });
}

// ── Current user ───────────────────────────────────────────────────────────

export function getMe(): Promise<MeResponse> {
  return request<MeResponse>('/me');
}

// ── Incoming documents ─────────────────────────────────────────────────────

export function listIncomingDocuments(page = 1, pageSize = 25): Promise<{ documents: Document[]; totalCount: number }> {
  return request<{ documents: Document[]; totalCount: number }>(`/documents/incoming?page=${page}&pageSize=${pageSize}`);
}

export function getIncomingCount(): Promise<{ count: number }> {
  return request<{ count: number }>('/documents/incoming/count');
}

// ── Rules ──────────────────────────────────────────────────────────────────

export function listRules(): Promise<Rule[]> {
  return request<Rule[]>('/rules');
}

export function createRule(dto: { title: string; selector: string; code: string }): Promise<Rule> {
  return request<Rule>('/rules', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateRule(id: string, dto: { title: string; selector: string; code: string }): Promise<Rule> {
  return request<Rule>(`/rules/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deleteRule(id: string): Promise<void> {
  return request<void>(`/rules/${id}`, { method: 'DELETE' });
}

export function applyRulesToDocument(
  documentId: string,
  preserveTitle = false,
  ruleId?: string,
): Promise<RuleApplicationResponse> {
  const query = new URLSearchParams();
  if (preserveTitle) query.set('preserveTitle', 'true');
  if (ruleId) query.set('ruleId', ruleId);
  const queryString = query.toString();
  return request<RuleApplicationResponse>(
    `/rules/apply/${documentId}${queryString ? `?${queryString}` : ''}`,
    { method: 'POST' },
  );
}

// ── Forms ─────────────────────────────────────────────────────────────────

export interface FormFieldInput {
  id?: string;
  name: string;
  dataType: FormFieldType;
  tagGroupId?: string | null;
  addTagToDocument?: boolean;
  uniqueForDocument?: boolean;
}

export function listForms(): Promise<FormDefinition[]> {
  return request<FormDefinition[]>('/forms');
}

export function createForm(dto: {
  title: string;
  fields: FormFieldInput[];
  isActive?: boolean;
}): Promise<FormDefinition> {
  return request<FormDefinition>('/forms', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateForm(id: string, dto: {
  title: string;
  fields: FormFieldInput[];
  isActive?: boolean;
}): Promise<FormDefinition> {
  return request<FormDefinition>(`/forms/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}

export function deleteForm(id: string): Promise<void> {
  return request<void>(`/forms/${id}`, { method: 'DELETE' });
}

export function listFormViews(): Promise<FormViewDefinition[]> {
  return request<FormViewDefinition[]>('/forms/views');
}

export function createFormView(dto: {
  title: string;
  viewType: string;
  definitionJson: string;
}): Promise<FormViewDefinition> {
  return request<FormViewDefinition>('/forms/views', {
    method: 'POST',
    body: JSON.stringify(dto),
  });
}

export function updateFormView(id: string, dto: {
  title: string;
  viewType: string;
  definitionJson: string;
}): Promise<FormViewDefinition> {
  return request<FormViewDefinition>(`/forms/views/${id}`, {
    method: 'PUT',
    body: JSON.stringify(dto),
  });
}

export function deleteFormView(id: string): Promise<void> {
  return request<void>(`/forms/views/${id}`, { method: 'DELETE' });
}

export function getFormViewData(id: string): Promise<FormViewData> {
  return request<FormViewData>(`/forms/views/${id}/data`);
}

export function clearIncomingFlag(documentId: string): Promise<void> {
  return request<void>(`/documents/${documentId}/clear-incoming`, { method: 'POST' });
}

// ── Upload Configs ───────────────────────────────────────────────────────────

export function listUploadConfigs(): Promise<UploadConfig[]> {
  return request<UploadConfig[]>('/upload-configs');
}

export function createUploadConfig(dto: {
  name: string;
  selector: string;
  checkIntervalMs: number;
  stabilityMs: number;
  timeoutMs: number;
}): Promise<UploadConfig> {
  return request<UploadConfig>('/upload-configs', { method: 'POST', body: JSON.stringify(dto) });
}

export function updateUploadConfig(id: string, dto: {
  name: string;
  selector: string;
  checkIntervalMs: number;
  stabilityMs: number;
  timeoutMs: number;
}): Promise<UploadConfig> {
  return request<UploadConfig>(`/upload-configs/${id}`, { method: 'PUT', body: JSON.stringify(dto) });
}

export function deleteUploadConfig(id: string): Promise<void> {
  return request<void>(`/upload-configs/${id}`, { method: 'DELETE' });
}

// ── Maintenance ────────────────────────────────────────────────────────────

export function getMaintenanceInfo(): Promise<MaintenanceInfo> {
  return request<MaintenanceInfo>('/maintenance/info');
}

export function createRestoreResetChallenge(): Promise<RestoreResetChallenge> {
  return request<RestoreResetChallenge>('/maintenance/restore-reset/challenge', {
    method: 'POST',
  });
}

export function resetForRestore(
  challengeId: string,
  confirmationCode: string,
): Promise<RestoreResetResult> {
  return request<RestoreResetResult>('/maintenance/restore-reset', {
    method: 'POST',
    body: JSON.stringify({ challengeId, confirmationCode }),
  });
}

export function startArchiveExport(): Promise<ArchiveExportJobStatus> {
  return request<ArchiveExportJobStatus>('/maintenance/export-archive/jobs', { method: 'POST' });
}

export function getBackupCapabilities(): Promise<BackupCapabilities> {
  return request<BackupCapabilities>('/maintenance/backup/capabilities');
}

export function startBackup(): Promise<BackupJobStatus> {
  return request<BackupJobStatus>('/maintenance/backup/jobs', { method: 'POST' });
}

export function streamBackup(
  jobId: string,
  onProgress: (status: BackupJobStatus) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(
      `${BASE}/maintenance/backup/jobs/${encodeURIComponent(jobId)}/events`,
      { withCredentials: true },
    );
    es.onmessage = (e: MessageEvent) => {
      try {
        const status = JSON.parse(e.data as string) as BackupJobStatus & { done?: boolean; error?: string };
        if (status.done && status.error) {
          es.close();
          reject(new Error(status.error));
          return;
        }
        onProgress(status);
        if (status.state === 'ready') {
          es.close();
          resolve();
        } else if (status.state === 'failed') {
          es.close();
          reject(new Error(status.error || 'Backup creation failed.'));
        }
      } catch {
        es.close();
        reject(new Error('Invalid backup status.'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Backup status stream failed.'));
    };
  });
}

export async function downloadBackupPart(jobId: string, partNumber: number): Promise<void> {
  const res = await fetch(
    `${BASE}/maintenance/backup/jobs/${encodeURIComponent(jobId)}/parts/${partNumber}`,
    { credentials: 'include' },
  );
  if (res.status === 401) {
    window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
    return new Promise<void>(() => {});
  }
  if (!res.ok) {
    let message = `Backup download failed (HTTP ${res.status})`;
    try {
      const body = await res.json() as { error?: string };
      if (body.error) message = body.error;
    } catch {
      // Keep the HTTP status as the error when the response is not JSON.
    }
    throw new Error(message);
  }

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const fileName = getDownloadFileName(
    disposition,
    `radish-backup-part-${partNumber}.zip`,
  );
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function uploadRequest<T>(path: string, body: FormData): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    body,
    credentials: 'include',
  });
  if (res.status === 401) {
    window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
    return new Promise<T>(() => {});
  }
  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const response = await res.json() as { error?: string };
      if (response.error) message = response.error;
    } catch {
      const text = await res.text().catch(() => '');
      if (text) message += `: ${text}`;
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

export function inspectBackup(files: File[]): Promise<BackupInspection> {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  return uploadRequest<BackupInspection>('/maintenance/backup/inspect', form);
}

export function restoreBackup(
  files: File[],
  backupId: string,
  mappings: BackupUserMapping[],
  mapAllToCurrentUser: boolean,
): Promise<BackupRestoreResult> {
  const form = new FormData();
  for (const file of files) form.append('files', file, file.name);
  form.append('backupId', backupId);
  form.append('mappings', JSON.stringify(mappings));
  form.append('mapAllToCurrentUser', String(mapAllToCurrentUser));
  return uploadRequest<BackupRestoreResult>('/maintenance/backup/restore', form);
}

/**
 * Opens the SSE status stream for a background archive export.
 * Calls onProgress until the job reaches a terminal state.
 */
export function streamArchiveExport(
  jobId: string,
  onProgress: (status: ArchiveExportJobStatus) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(
      `${BASE}/maintenance/export-archive/jobs/${encodeURIComponent(jobId)}/events`,
      { withCredentials: true },
    );
    es.onmessage = (e: MessageEvent) => {
      try {
        const value = JSON.parse(e.data as string) as ArchiveExportJobStatus & { done?: boolean; message?: string };
        if (value.done && value.error) {
          es.close();
          reject(new Error(value.error));
          return;
        }
        const status = value as ArchiveExportJobStatus;
        onProgress(status);
        if (status.state === 'ready') {
          es.close();
          resolve();
        } else if (status.state === 'failed') {
          es.close();
          reject(new Error(status.error || 'Archive export failed.'));
        }
      } catch {
        es.close();
        reject(new Error('Invalid archive export status'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('Archive export status stream failed'));
    };
  });
}

/** Downloads a completed background archive export and triggers a browser "Save As". */
export async function downloadArchiveExport(jobId: string): Promise<void> {
  const res = await fetch(
    `${BASE}/maintenance/export-archive/jobs/${encodeURIComponent(jobId)}/download`,
    { credentials: 'include' },
  );
  if (res.status === 401) {
    window.location.href = apiUrl(`/auth/login?returnUrl=${encodeURIComponent(window.location.pathname)}`);
    return new Promise<void>(() => {});
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Export failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') ?? '';
  const fileName = getDownloadFileName(
    disposition,
    `radish-offline-archive-${new Date().toISOString().slice(0, 10)}.zip`,
  );

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Opens an SSE stream for a maintenance operation.
 * Calls onProgress for each event, resolves when the stream ends.
 */
export function streamMaintenance(
  path: string,
  onProgress: (evt: MaintenanceProgressEvent) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const es = new EventSource(`${getBffBaseUrl()}${path}`, { withCredentials: true });
    es.onmessage = (e: MessageEvent) => {
      try {
        const evt = JSON.parse(e.data as string) as MaintenanceProgressEvent;
        onProgress(evt);
        if (evt.done) {
          es.close();
          resolve();
        }
      } catch {
        es.close();
        reject(new Error('Invalid SSE data'));
      }
    };
    es.onerror = () => {
      es.close();
      reject(new Error('SSE connection error'));
    };
  });
}

// ── Utilities ──────────────────────────────────────────────────────────────

/** Formats a byte count as a human-readable string (e.g. "1.2 MB", "847 KB"). */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
